import {
    BaseDropdown,
    BaseDropdownPopupOptions,
    DropdownTriggerRenderer,
} from 'components/dropdown/BaseDropdown';
import {
    DropdownDataSource,
    DropdownFilterConfig,
    DropdownResultsRenderer,
} from 'components/dropdown/DropdownPopup';
import { DropdownTrigger } from 'components/dropdown/DropdownTrigger';
import { DropdownPaddingOverride } from 'components/dropdown/styled';
import { UseDropdownControlResult } from 'components/dropdown/useDropdownControl';
import { CSSProperties, ReactNode, Ref, useCallback, useMemo, useRef } from 'react';
import { InfoPopup } from 'shared/info_popup/InfoPopup';
import styled, { css } from 'styled-components';
import { stopEvent } from 'utils/event';
import { mergeRefs } from 'utils/mergeRefs';
import { pointerDownClickHandler } from 'utils/pointer_events';

export type DropdownOptionProps = Readonly<{
    $isHighlighted: boolean;
    $isSelected: boolean;
    $isDisabled: boolean;
}>;

const outlineStyle = css`
    outline: 1px solid var(--text-primary);
`;

export const DropdownItemsList = styled.ul`
    margin: 0;
    padding: 0;
    list-style-type: none;
`;

const selectedStyle = css<DropdownOptionProps>`
    background: var(--action-selected);
`;

export const DropdownOption = styled.li<DropdownOptionProps>`
    text-align: left;
    cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
    display: block;
    background: ${({ $isHighlighted, $isDisabled }) =>
        $isHighlighted && !$isDisabled
            ? 'var(--action-hover)'
            : 'var(--background-paper-elevation-8)'};
    color: ${({ $isDisabled }) =>
        $isDisabled ? 'var(--text-disabled)' : 'var(--text-primary)'};
    padding: 1rem 2rem;

    border-radius: 0;

    // The default :focus-visible is wrong when you first open a dropdown that
    // already has an item selected. Hide in in that case until the user
    // provides input
    &:focus-visible {
        ${({ $isHighlighted }) => ($isHighlighted ? outlineStyle : 'outline: none')};
    }

    ${({ $isSelected }) => ($isSelected ? selectedStyle : '')}

    &:active {
        ${selectedStyle};
    }
`;

export type DropdownItemRenderInfo<TData> = Readonly<{
    /**
     * The item to be rendered.
     */
    item: TData;
    /**
     * The index of the item.
     */
    index: number;
    /**
     * If the item is currently highlighted.
     */
    isHighlighted: boolean;
    /**
     * If the item is currently selected.
     */
    isSelected: boolean;
}>;

export type DropdownItemRenderer<TData> = (
    info: DropdownItemRenderInfo<TData>
) => ReactNode;

export type DropdownLabelRenderInfo<TData> = Readonly<{
    /**
     * The current value.
     */
    value: TData | undefined;
    /**
     * If dropdown is currently open.
     */
    isOpen: boolean;
}>;

export type DropdownLabelRenderer<TData> = (
    info: DropdownLabelRenderInfo<TData>
) => ReactNode;

export type DropdownControl = UseDropdownControlResult;

/**
 * Utility for rendering primitive dropdown items.
 */
export function PrimitiveDropdownItem<TData extends string | number>({
    item,
}: DropdownItemRenderInfo<TData>): ReactNode {
    return <>{item}</>;
}

export type DropdownTriggerOptions = Readonly<{
    /**
     * Aria label for dropdown trigger.
     */
    ariaLabel?: string;
    /**
     * Hides the chevron icon.
     * @default false
     */
    hideChevron?: boolean;
    /**
     * Classes to apply to trigger component.
     */
    className?: string;
    /**
     * Ref for trigger button.
     */
    triggerRef?: Ref<HTMLButtonElement>;
    /**
     * Customize the trigger's padding.
     */
    paddingOverride?: DropdownPaddingOverride;
    /**
     * Overrides color of the chevron.
     */
    chevronColor?: string;
    /**
     * Styles to apply to the trigger component
     */
    style?: CSSProperties;
}>;

export type DisabledOptionInfo = Readonly<{ key: number | string; message: string }>;

export type DropdownProps<TData> = Readonly<{
    id?: string;
    /**
     * Callback to render the label for the dropdown trigger.
     */
    renderLabel: DropdownLabelRenderer<TData>;
    /**
     * Callback used to get a unique identifier for each item.
     */
    dataKey: (item: TData) => string | number;
    /**
     * The data for the dropdown (can be an array or a dropdown data source object).
     */
    data: TData[] | DropdownDataSource<TData> | undefined;
    /**
     * Callback to render a dropdown item.
     */
    renderItem: DropdownItemRenderer<TData>;
    /**
     * The current value of the dropdown.
     */
    value: TData | undefined;
    /**
     * Callback for when the selected value changes.
     */
    onChange: (newValue: TData) => void;
    /**
     * Optional callback to handle values being cleared. When supplied, allows clearing via a button on the trigger.
     */
    onClear?: () => void;
    /**
     * Filter configuration of the dropdown.  If no configuration is supplied, filter will not be visible.
     */
    filterable?: DropdownFilterConfig;
    /**
     * If the dropdown is disabled.
     */
    isDisabled?: boolean;
    /**
     * Ref to gain access to the internal state of the dropdown.
     */
    controlRef?: Ref<DropdownControl>;
    /**
     * Additional popup configuration.
     */
    popup?: BaseDropdownPopupOptions;
    /**
     * Additional trigger configuration.
     */
    trigger?: DropdownTriggerOptions;
    /**
     * Optional item to add to the end of the list. Can be used to allow adding an item not in the list.
     */
    trailingItem?: { element: ReactNode; onClick: () => void };
    /**
     * Optional list of items to be disabled and messages for why they are disabled.
     */
    disabledItems?: DisabledOptionInfo[];
}>;

export function Dropdown<TData>({
    id,
    renderLabel,
    dataKey,
    data,
    renderItem,
    value,
    onChange,
    onClear,
    filterable,
    isDisabled,
    controlRef,
    popup,
    trigger,
    trailingItem,
    disabledItems,
}: DropdownProps<TData>) {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const dataKeyRef = useRef(dataKey);
    const renderTrigger = useCallback<DropdownTriggerRenderer>(
        (info) => {
            const handlers = pointerDownClickHandler(
                () => info.setIsPopupOpen((isOpen) => !isOpen),
                isDisabled
            );

            return (
                <DropdownTrigger
                    id={id}
                    ref={mergeRefs(triggerRef, trigger?.triggerRef)}
                    onPointerDown={handlers.onPointerDown}
                    onPointerUp={handlers.onPointerUp}
                    onClick={(event) => {
                        stopEvent(event);
                        handlers.onClick(event);
                    }}
                    onClear={
                        onClear !== undefined && value !== undefined ? onClear : undefined
                    }
                    onBlur={info.onBlur}
                    disabled={isDisabled}
                    ariaLabel={trigger?.ariaLabel}
                    hideChevron={trigger?.hideChevron}
                    className={trigger?.className}
                    paddingOverride={trigger?.paddingOverride}
                    chevronColor={trigger?.chevronColor}
                    style={trigger?.style}
                    isPlaceholder={value === undefined}
                >
                    {renderLabel({ isOpen: info.isPopupOpen, value })}
                </DropdownTrigger>
            );
        },
        [
            id,
            trigger?.triggerRef,
            trigger?.ariaLabel,
            trigger?.hideChevron,
            trigger?.className,
            trigger?.paddingOverride,
            trigger?.chevronColor,
            trigger?.style,
            onClear,
            value,
            isDisabled,
            renderLabel,
        ]
    );

    const selectedValueKey = useMemo(
        () => (value !== undefined ? dataKeyRef.current(value) : undefined),
        [value]
    );

    const renderResults = useCallback<DropdownResultsRenderer<TData>>(
        (items) => (
            <div>
                {items.map(
                    ({ item, isHighlighted, itemRef, onSelect, onHover }, index) => {
                        const itemKey = dataKey(item);
                        const isSelected = itemKey === selectedValueKey;
                        const disabledMessage = disabledItems?.find(
                            (item) => item.key === itemKey
                        )?.message;

                        return (
                            <DropdownOption
                                role="option"
                                ref={itemRef}
                                className="word-wrap-anywhere"
                                key={itemKey}
                                onPointerEnter={onHover}
                                onPointerMove={onHover}
                                $isHighlighted={isHighlighted}
                                $isSelected={isSelected}
                                $isDisabled={disabledMessage !== undefined}
                                aria-selected={isSelected}
                                onClick={(event) => {
                                    stopEvent(event);

                                    if (!disabledMessage) {
                                        onSelect();
                                    }
                                }}
                            >
                                {renderItem({
                                    item,
                                    isHighlighted,
                                    isSelected,
                                    index,
                                })}
                                {disabledMessage && (
                                    <InfoPopup title="Item disabled" type="error">
                                        {disabledMessage}
                                    </InfoPopup>
                                )}
                            </DropdownOption>
                        );
                    }
                )}
            </div>
        ),
        [dataKey, disabledItems, renderItem, selectedValueKey]
    );

    return (
        <BaseDropdown<TData>
            controlRef={controlRef}
            data={data}
            filterable={filterable}
            renderTrigger={renderTrigger}
            renderResults={renderResults}
            onSelect={onChange}
            popup={{
                ...popup,
            }}
            triggerRef={triggerRef}
            TrailingItemComponent={
                trailingItem
                    ? ({ isHighlighted, itemRef, onHover, onClick }) => {
                          const itemKey = 'trailing-option';
                          const isSelected = itemKey === selectedValueKey;
                          const disabledMessage = disabledItems?.find(
                              (item) => item.key === itemKey
                          )?.message;

                          return (
                              <DropdownOption
                                  role="option"
                                  ref={itemRef}
                                  className="word-wrap-anywhere"
                                  key={itemKey}
                                  onPointerEnter={onHover}
                                  onPointerMove={onHover}
                                  $isHighlighted={isHighlighted}
                                  $isSelected={isSelected}
                                  $isDisabled={disabledMessage !== undefined}
                                  aria-selected={isSelected}
                                  onClick={(event) => {
                                      stopEvent(event);

                                      if (!disabledMessage) {
                                          trailingItem.onClick();
                                          onClick();
                                      }
                                  }}
                              >
                                  {trailingItem.element}
                                  {disabledMessage && (
                                      <InfoPopup title="Item disabled" type="error">
                                          {disabledMessage}
                                      </InfoPopup>
                                  )}
                              </DropdownOption>
                          );
                      }
                    : undefined
            }
        />
    );
}
